/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { Fragment, useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { ChevronUpDownIcon } from "@heroicons/react/24/solid";
import {
  PacketLossResult,
  PacketLossMonitor,
  PacketLossResultDetail,
} from "@/types/types";
import { getPacketLossHistoryDetail } from "@/api/packetloss";
import { MTRResultsDisplay } from "./MTRResultsDisplay";
import { formatRTT, parseMTRData } from "../utils/packetLossUtils";
import { formatDateTimeWithSettings } from "@/utils/timeSettings";

interface MonitorResultsTableProps {
  historyList: PacketLossResult[];
  selectedMonitor: PacketLossMonitor;
  totalCount: number;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
}

export const MonitorResultsTable: React.FC<MonitorResultsTableProps> = ({
  historyList,
  selectedMonitor,
  totalCount,
  hasMore,
  isFetchingMore,
  onLoadMore,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [detailCache, setDetailCache] = useState<Map<number, PacketLossResultDetail>>(
    new Map(),
  );
  const [loadingDetails, setLoadingDetails] = useState<Set<number>>(new Set());
  const [detailErrors, setDetailErrors] = useState<Set<number>>(new Set());
  const expandedRowsRef = useRef(expandedRows);

  useEffect(() => {
    expandedRowsRef.current = expandedRows;
  }, [expandedRows]);

  const toggleExpanded = async (result: PacketLossResult) => {
    const isExpanded = expandedRows.has(result.id);

    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.delete(result.id);
      } else {
        newSet.add(result.id);
      }
      return newSet;
    });

    if (isExpanded) {
      setDetailCache((prev) => {
        const next = new Map(prev);
        next.delete(result.id);
        return next;
      });
      setDetailErrors((prev) => {
        const next = new Set(prev);
        next.delete(result.id);
        return next;
      });
      return;
    }

    if (
      !result.usedMtr ||
      detailCache.has(result.id) ||
      loadingDetails.has(result.id)
    ) {
      return;
    }

    setLoadingDetails((prev) => new Set(prev).add(result.id));
    setDetailErrors((prev) => {
      const next = new Set(prev);
      next.delete(result.id);
      return next;
    });

    try {
      const detail = await getPacketLossHistoryDetail(
        selectedMonitor.id,
        result.id,
      );

      if (!expandedRowsRef.current.has(result.id)) {
        return;
      }

      setDetailCache((prev) => {
        const next = new Map(prev);
        next.set(result.id, detail);
        return next;
      });
    } catch (error) {
      console.error("Failed to fetch MTR detail:", error);

      if (!expandedRowsRef.current.has(result.id)) {
        return;
      }

      setDetailErrors((prev) => new Set(prev).add(result.id));
    } finally {
      setLoadingDetails((prev) => {
        const next = new Set(prev);
        next.delete(result.id);
        return next;
      });
    }
  };

  const formatDate = (dateStr: string) => {
    return formatDateTimeWithSettings(dateStr);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-gray-700 dark:text-gray-300 font-medium">
          Recent Results {totalCount > 0 && `(${totalCount} total)`}
        </h3>
        {historyList.some((result) => result.usedMtr) && (
          <p className="text-xs text-gray-500 dark:text-gray-500 hidden md:block">
            Click MTR rows to view hop-by-hop details
          </p>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-300 dark:border-gray-800">
              <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">
                Time
              </th>
              <th className="text-center py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">
                Loss
              </th>
              <th className="text-center py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">
                Avg RTT
              </th>
              <th className="text-center py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">
                Min RTT
              </th>
              <th className="text-center py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">
                Max RTT
              </th>
              <th className="text-center py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">
                Sent/Recv
              </th>
              <th className="text-center py-2 px-3 text-gray-600 dark:text-gray-400 font-medium">
                Mode
              </th>
            </tr>
          </thead>
          <tbody>
            {historyList.map((result) => {
              const detail = detailCache.get(result.id);
              const mtrData = parseMTRData(detail?.mtrData);
              const isExpanded = expandedRows.has(result.id);
              const isLoadingDetail = loadingDetails.has(result.id);
              const hasDetailError = detailErrors.has(result.id);

              return (
                <Fragment key={result.id}>
                  <motion.tr
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`border-b border-gray-300/50 dark:border-gray-800/50 transition-colors ${
                      result.usedMtr
                        ? "cursor-pointer hover:bg-purple-500/5 dark:hover:bg-purple-500/10 hover:border-purple-500/20 dark:hover:border-purple-500/30"
                        : "hover:bg-gray-200/30 dark:hover:bg-gray-800/30"
                    }`}
                    onClick={() => {
                      if (result.usedMtr) {
                        void toggleExpanded(result);
                      }
                    }}
                  >
                    <td className="py-2 px-3 text-gray-700 dark:text-gray-300">
                      {formatDate(result.createdAt)}
                    </td>
                    <td
                      className={`py-2 px-3 text-center font-mono ${
                        result.packetLoss > selectedMonitor.threshold
                          ? "text-red-600 dark:text-red-400 font-medium"
                          : "text-emerald-600 dark:text-emerald-400"
                      }`}
                    >
                      {result.packetLoss.toFixed(1)}%
                    </td>
                    <td className="py-2 px-3 text-center font-mono text-blue-600 dark:text-blue-400">
                      {formatRTT(result.avgRtt)}ms
                    </td>
                    <td className="py-2 px-3 text-center font-mono text-emerald-600 dark:text-emerald-400">
                      {formatRTT(result.minRtt)}ms
                    </td>
                    <td className="py-2 px-3 text-center font-mono text-yellow-600 dark:text-yellow-400">
                      {formatRTT(result.maxRtt)}ms
                    </td>
                    <td className="py-2 px-3 text-center text-gray-600 dark:text-gray-400">
                      {result.packetsSent}/{result.packetsRecv}
                    </td>
                    <td className="py-2 px-3 text-center">
                      {result.usedMtr ? (
                        <div className="inline-flex items-center gap-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 transition-colors cursor-pointer">
                            MTR
                            {result.privilegedMode !== undefined && (
                              <span className="ml-1">
                                ({result.privilegedMode ? "ICMP" : "UDP"})
                              </span>
                            )}
                            {result.hopCount && (
                              <span className="hidden 2xl:inline">
                                {" "}
                                - {result.hopCount} hops
                              </span>
                            )}
                          </span>
                          <motion.div
                            animate={{
                              rotate: isExpanded ? 180 : 0,
                            }}
                            transition={{ duration: 0.2 }}
                            className="text-purple-600 dark:text-purple-400"
                          >
                            <ChevronUpDownIcon className="w-3 h-3" />
                          </motion.div>
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                          ICMP
                        </span>
                      )}
                    </td>
                  </motion.tr>

                  {/* Expandable MTR Details Row */}
                  {result.usedMtr && isExpanded && isLoadingDetail && (
                    <tr className="bg-gray-100/50 dark:bg-gray-900/50">
                      <td colSpan={8} className="p-4 text-sm text-gray-600 dark:text-gray-400">
                        Loading MTR details...
                      </td>
                    </tr>
                  )}
                  {result.usedMtr && isExpanded && hasDetailError && (
                    <tr className="bg-gray-100/50 dark:bg-gray-900/50">
                      <td colSpan={8} className="p-4 text-sm text-red-600 dark:text-red-400">
                        Failed to load MTR details.
                      </td>
                    </tr>
                  )}
                  {result.usedMtr && mtrData && (
                    <MTRResultsDisplay
                      mtrData={mtrData}
                      isExpanded={isExpanded}
                      isMobile={false}
                    />
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        {/* Load More Button */}
        {hasMore && (
          <div className="flex justify-center mt-4">
            <button
              onClick={(event) => {
                event.stopPropagation();
                onLoadMore();
              }}
              disabled={isFetchingMore}
              className="px-4 py-2 bg-gray-200/30 dark:bg-gray-800/30 border border-gray-300/50 dark:border-gray-900/50 text-gray-600/50 dark:text-gray-300/50 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-300/50 dark:hover:bg-gray-800/50 transition-colors"
            >
              {isFetchingMore ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {historyList.map((result) => {
          const detail = detailCache.get(result.id);
          const mtrData = parseMTRData(detail?.mtrData);
          const isExpanded = expandedRows.has(result.id);
          const isLoadingDetail = loadingDetails.has(result.id);
          const hasDetailError = detailErrors.has(result.id);

          return (
            <motion.div
              key={result.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`bg-gray-200/50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-300 dark:border-gray-800 ${
                result.usedMtr
                  ? "cursor-pointer hover:bg-gray-200/70 dark:hover:bg-gray-800/70 transition-colors"
                  : ""
              }`}
              onClick={() => {
                if (result.usedMtr) {
                  void toggleExpanded(result);
                }
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="text-gray-700 dark:text-gray-300 text-sm font-medium">
                  {formatDate(result.createdAt)}
                </div>
                <div className="flex items-center gap-2">
                  {result.usedMtr && (
                    <>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-500/10 text-purple-600 dark:text-purple-400">
                        MTR
                        {result.privilegedMode !== undefined && (
                          <span className="ml-1 text-xs">
                            ({result.privilegedMode ? "ICMP" : "UDP"})
                          </span>
                        )}
                      </span>
                      <motion.div
                        animate={{
                          rotate: isExpanded ? 180 : 0,
                        }}
                        transition={{ duration: 0.2 }}
                        className="text-gray-500 dark:text-gray-400"
                      >
                        <ChevronUpDownIcon className="w-4 h-4" />
                      </motion.div>
                    </>
                  )}
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      result.packetLoss > selectedMonitor.threshold
                        ? "bg-red-500/10 text-red-600 dark:text-red-400"
                        : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                    }`}
                  >
                    {result.packetLoss.toFixed(1)}% loss
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    Avg RTT:
                  </span>
                  <span className="text-blue-600 dark:text-blue-400 font-mono">
                    {formatRTT(result.avgRtt)}ms
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    Min RTT:
                  </span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-mono">
                    {formatRTT(result.minRtt)}ms
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    Max RTT:
                  </span>
                  <span className="text-yellow-600 dark:text-yellow-400 font-mono">
                    {formatRTT(result.maxRtt)}ms
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    Packets:
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    {result.packetsRecv}/{result.packetsSent}
                  </span>
                </div>
              </div>

              {/* Expandable MTR Details */}
              {result.usedMtr && isExpanded && isLoadingDetail && (
                <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                  Loading MTR details...
                </div>
              )}

              {result.usedMtr && isExpanded && hasDetailError && (
                <div className="mt-3 text-sm text-red-600 dark:text-red-400">
                  Failed to load MTR details.
                </div>
              )}

              {result.usedMtr && mtrData && (
                <MTRResultsDisplay
                  mtrData={mtrData}
                  isExpanded={isExpanded}
                  isMobile={true}
                />
              )}
            </motion.div>
          );
        })}

        {/* Load More Button for Mobile */}
        {hasMore && (
          <div className="flex justify-center mt-4">
            <button
              onClick={(event) => {
                event.stopPropagation();
                onLoadMore();
              }}
              disabled={isFetchingMore}
              className="px-4 py-2 bg-gray-200/30 dark:bg-gray-800/30 border border-gray-300/50 dark:border-gray-900/50 text-gray-600/50 dark:text-gray-300/50 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-300/50 dark:hover:bg-gray-800/50 transition-colors"
            >
              {isFetchingMore ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
